import {ExternalLinkType, InternalLinkType} from '../../../../types'
import {FormDataType, FormStepType, FormType} from '../../../blocks/FormBlock'
import React, {useState} from 'react'

import {Blocks} from '../../../blocks/Blocks'
import {DeliveryType} from './Cart'
import {OptionType} from '../../../ui/Dropdown'
import {Theme} from '../../../Theme'
import organisationNL from './orderform/steps/organisationNL'
import personal from './orderform/steps/personal'
import schoolNL from './orderform/steps/schoolNL'
import step1 from './orderform/steps/step1'
import {style} from '../../../../util/style'
import {useCart} from '../../../../util/cart'
import {useHistory} from 'react-router'

const styles = style('orderform')

type clientTypeType = 'personal' | 'school' | 'organisation'

type schoolDetailsType = {
	name: string
	street: string
	number: string
	box: string
	zip: number
	city: string
	key: number
}

export type RegionalServiceType = {
	id: number
	title: string
	path: string
	phone: string
	items: Array<{
		date: string
		description: string
	}>
	contact_details_url?: InternalLinkType | ExternalLinkType
}

export type FormProducts = Array<FormProduct>
export type FormProduct = {
	id: number
	quantity?: number
	variants?: Array<{id: number; quantity: number}>
}
export const OrderForm: React.FC<{
	schools: {
		[key: string]: Array<schoolDetailsType>
	}
	selectedDelivery: DeliveryType
	products: FormProducts
	reCaptchaKey: string
	regionalServices: Array<RegionalServiceType>
	formCompleted: () => void
}> = ({
	reCaptchaKey,
	schools,
	regionalServices,
	products,
	selectedDelivery
}) => {
	const [steps, setSteps] = useState<Array<FormStepType>>([
		step1 as FormStepType
	])
	const [clientType, setClientType] = useState<clientTypeType>()
	const deliveryAddressFields = [
		'2-3-16',
		'2-3-0',
		'2-3-6',
		'2-3-7',
		'2-3-8',
		'2-3-9',
		'2-3-10'
	]
	const personalAddressFields: Array<string> = [
		'2-1-6',
		'2-1-7',
		'2-1-8',
		'2-1-9',
		'2-1-10'
	]
	const invoiceAddressFields: Array<string> = [
		'2-2-6',
		'2-2-7',
		'2-2-8',
		'2-2-9',
		'2-2-10'
	]

	const [hideFields, setHideFields] = useState<Array<string>>([
		...deliveryAddressFields,
		...personalAddressFields,
		'2-6-20' // schoolOther
	])

	const defaultDelivery: 'delivery' | 'takeaway' = selectedDelivery.type
		? selectedDelivery.type
		: 'delivery'
	const [delivery, setDelivery] = useState<'delivery' | 'takeaway'>(
		defaultDelivery
	)
	const [deliveryPath, setDeliveryPath] = useState<string>(
		selectedDelivery.path
	)
	const [schoolCity, setSchoolCity] = useState<string>('-')
	const [school, setSchool] = useState<string>('-')
	const [regionalService, setRegionalService] = useState<string>(
		regionalServices[0].path
	)
	const [defaultData, setDefaultData] = useState<{
		products: string
		delivery: 'delivery' | 'takeaway'
		deliveryPath: string
		'1-1-1'?: string
		'2-6-13'?: string
		'2-6-14'?: string
		'2-6-6'?: string
		'2-6-7'?: string
		'2-6-8'?: string
		'2-6-9'?: string
		'2-6-10'?: string
		'2-2-22'?: string
		'2-4-2'?: string
		'2-4-4'?: string
		'2-4-5'?: string
	}>({
		products: JSON.stringify(products),
		delivery,
		deliveryPath
	})

	const defaultOptionType: OptionType = {
		amount: null,
		path: '-',
		title: 'Maak een selectie' //todo: translate
	}

	React.useEffect(() => {
		defineStep2()
	}, [
		clientType,
		delivery,
		hideFields,
		schoolCity,
		defaultData,
		regionalService
	])

	/**
	 * Define the second step, depending on the value of field 1-1-1
	 * @param value
	 */
	function defineStep2() {
		const tempSteps: Array<FormStepType> = [steps[0]]
		let step2: FormStepType
		switch (clientType) {
			case 'school':
				step2 = {...(schoolNL as FormStepType)}
				// add school cities to the schoolCity list
				// add school names to the
				step2 = {
					...step2,
					sections: step2.sections.map((section) => {
						return {
							...section,
							fields: section.fields.map((field: FormType) => {
								if (field.name === 'schoolCity' && field.channel === 'select') {
									field.options = [
										defaultOptionType,
										...Object.keys(schools).map((key) => {
											return {
												amount: null,
												path: key,
												title: key
											}
										})
									]
								}
								if (field.name === 'school' && field.channel === 'select') {
									if (schools[schoolCity]) {
										field.options = [
											defaultOptionType,
											...schools[schoolCity].map((row) => {
												return {
													amount: null,
													path: `${row.key}`,
													title: `${row.name} (${row.street} ${row.number}, ${row.zip} ${row.city})`
												}
											}),
											{
												amount: null,
												path: 'other',
												title: 'Andere' //todo: translate
											}
										]
									} else {
										field.options = [defaultOptionType]
									}
								}
								return field
							})
						}
					})
				}
				break
			case 'organisation':
				step2 = {...(organisationNL as FormStepType)}
				break
			default:
				step2 = {...(personal as FormStepType)}
		}

		// remove sections/fields depending on other field values
		const sections = [...step2.sections]
			.filter((section) => {
				// define if delivery or takeaway should be visible
				// remove delivery section: id 3
				// remove takeaway section: id 4
				return section.id !== (delivery === 'delivery' ? 4 : 3)
			})
			.map((section) => {
				const fields = [...section.fields]
					.map((field) => {
						const f = {...field}
						// 2-4-2 => takeaway regional services
						if (f.parent_id === 4 && f.id === 2 && f.channel === 'select') {
							f.options = regionalServices.map((rs) => {
								return {path: rs.path, title: rs.title}
							})
							return f
						}
						// 2-4-3 => takeaway phone number text
						if (f.parent_id === 4 && f.id === 3 && f.channel === 'text') {
							f.text = f.text.replace(
								'{phone}',
								regionalServices.find((rg) => rg.path === regionalService).phone
							)
							const serviceUrl = regionalServices.find(
								(rg) => rg.path === regionalService
							).contact_details_url
							if (serviceUrl) {
								const url = serviceUrl.url
								f.text += ` <a href="${url}" target="_blank" class="mod-inherit" title="${
									serviceUrl.description
										? serviceUrl.description
										: serviceUrl.title
								}">${
									serviceUrl.description
										? serviceUrl.description
										: serviceUrl.title
								}</a>`
							}
							return f
						}
						// 2-4-4 => takeaway launch dates
						if (f.parent_id === 4 && f.id === 4 && f.channel === 'select') {
							const options = regionalServices
								.find((rg) => rg.path === regionalService)
								.items.map((item) => {
									let description: string = item.description
									if (description !== '') {
										description = ` - ${description}`
									}
									return {
										path: item.date,
										title: `${item.date}${description}`
									}
								})
							if (options.length === 0) return null
							f.options = options
							return f
						}
						return f
					})
					.filter((field) => field !== null)
				return {
					...section,
					fields
				}
			})

		step2 = {
			...step2,
			sections
		}

		tempSteps.push(step2)

		setSteps(
			tempSteps.map((step) => {
				return {
					...step,
					sections: step.sections.map((section) => {
						return {
							...section,
							fields: section.fields.filter(
								(field) =>
									!hideFields.includes(`${step.id}-${section.id}-${field.id}`) // remove hidden fields from the view
							)
						}
					})
				}
			})
		)
	}

	function handleOnChange(
		key: string,
		value: string | boolean,
		currentFormData: FormDataType
	) {
		switch (key) {
			case '1-1-1': // soort klant, ik bestel voor
				setClientType(value as clientTypeType)
				if (value === 'personal') {
					// show invoice address fields
					setHideFields([
						...hideFields.filter(
							(hide: string) => !invoiceAddressFields.includes(hide)
						)
					])
				} else {
					if (
						currentFormData['2-2-22'] &&
						currentFormData['2-2-22'] === 'other'
					) {
						// show invoice address fields
						setHideFields([
							...hideFields.filter(
								(hide: string) => !invoiceAddressFields.includes(hide)
							)
						])
					} else {
						// hide invoice address fields
						setHideFields([
							...new Set([...hideFields, ...invoiceAddressFields])
						])
					}
				}
				break

			case '2-1-15': // zelfde adresgegevens als klantengegevens
				if (value !== 'other')
					setHideFields([...new Set([...hideFields, ...personalAddressFields])])
				// hide the fields
				else
					setHideFields([
						...hideFields.filter(
							(hide: string) => !personalAddressFields.includes(hide)
						) // show the fields
					])
				break

			case '2-3-18': // levering ander adres
				if (value !== 'other') {
					setHideFields([...new Set([...hideFields, ...deliveryAddressFields])])
				} else {
					setHideFields([
						...hideFields.filter(
							(hide: string) => !deliveryAddressFields.includes(hide)
						)
					])
				}
				break

			case '2-2-22': // invoice ander adres
				if (clientType === 'school' || clientType === 'organisation') {
					if (value !== 'other') {
						setHideFields([
							...new Set([...hideFields, ...invoiceAddressFields])
						])
					} else {
						setHideFields([
							...hideFields.filter(
								(hide: string) => !invoiceAddressFields.includes(hide)
							)
						])
					}
				}
				break

			case '2-4-5': // change takeaway section to delivery
				if (value) {
					setDelivery('delivery')
					setDeliveryPath(null) // when switched to delivery, the new delivery path is unknown because the user kan not make a choice anymore
					setDefaultData({
						...defaultData,
						delivery: 'delivery',
						deliveryPath: null,
						'2-4-2': null,
						'2-4-4': null,
						'2-4-5': null
					})
				}
				break

			case '2-6-13': // a schoolCity was selected
				if (value) {
					setDefaultData({
						...defaultData,
						'2-6-14': '-',
						'2-6-6': '',
						'2-6-7': '',
						'2-6-8': '',
						'2-6-9': '',
						'2-6-10': ''
					})
					setSchoolCity(value as string)
				}
				break

			case '2-6-14': // a school was selected
				if (value) {
					const schoolData = schools[schoolCity].find(
						(s) => s.key === parseInt(value as string, 10)
					)
					if (schoolData) {
						setDefaultData({
							...defaultData,
							'2-6-14': `${value}`,
							'2-6-6': schoolData.street,
							'2-6-7': schoolData.number,
							'2-6-8': schoolData.box,
							'2-6-9': `${schoolData.zip}`,
							'2-6-10': schoolData.city
						})
					} else {
						setDefaultData({
							...defaultData,
							'2-6-14': `${value}`,
							'2-6-6': null,
							'2-6-7': null,
							'2-6-8': null,
							'2-6-9': null,
							'2-6-10': value === 'other' ? schoolCity : null
						})
					}
					if (value === 'other') {
						// show schoolOther field
						setHideFields([
							...hideFields.filter((hide: string) => !['2-6-20'].includes(hide))
						])
					} else {
						// hide schoolOther field
						setHideFields([...new Set([...hideFields, '2-6-20'])])
					}
				}
				setSchool(value as string)
				break

			case '2-4-2': // takeaway regional service changed
				setRegionalService(value as string)
				setDefaultData({
					...defaultData,
					'2-4-4': null
				})
				break
		}
	}

	return (
		<div className={styles()}>
			<Theme.Container>
				<Blocks
					blocks={[
						{
							type: 'form' as 'form',
							path: '',
							reCaptchaKey,
							id: 1,
							pageId: 0,
							steps,
							data: defaultData,
							onChange: handleOnChange,
							useEffect: true,
							onSubmit: (response) => {
								if (response.uuid && response.thanksPage) {
									new Promise((resolve) => {
										const {clearCart} = useCart()
										resolve(clearCart())
									}).then((cart) => {
										const history = useHistory()
										history.push(
											`${response.thanksPage}?order=${response.uuid}`
										)
									})
								} else {
									console.error('No uuid or thanksPage defined', response)
								}
							}
						}
					]}
				/>
			</Theme.Container>
		</div>
	)
}
